import { makeAutoObservable, runInAction } from 'mobx';
import {
  emailApi,
  type Email,
  type EmailHistory,
  type SendEmailParams,
} from '../api/email_api';

export class EmailStore {
  // Emails indexed by key
  emails: Map<string, Email> = new Map();
  // Emails indexed by association (associated_type:associated_uuid -> email keys)
  emailsByAssociation: Map<string, string[]> = new Map();
  // Email history indexed by email uuid
  history: Map<string, EmailHistory[]> = new Map();

  // Loading states
  loading = false;
  historyLoading = false;
  sending = false;

  // Error state
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed getters
  getEmailsByAssociation(
    associatedType: string,
    associatedUuid: string
  ): Email[] {
    const key = `${associatedType}:${associatedUuid}`;
    const emailKeys = this.emailsByAssociation.get(key) || [];
    return emailKeys
      .map((k) => this.emails.get(k))
      .filter((e): e is Email => e !== undefined)
      .sort(
        (a, b) =>
          new Date(b.date_created).getTime() -
          new Date(a.date_created).getTime()
      );
  }

  getEmailByKey(key: string): Email | undefined {
    return this.emails.get(key);
  }

  getHistory(emailUuid: string): EmailHistory[] {
    return this.history.get(emailUuid) || [];
  }

  // Actions
  clearError(): void {
    this.error = null;
  }

  /**
   * Fetch emails for an association (request or collab)
   */
  async fetchByAssociation(
    associatedType: string,
    associatedUuid: string
  ): Promise<void> {
    const associationKey = `${associatedType}:${associatedUuid}`;

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      let cursor: string | undefined;
      const allEmailKeys: string[] = [];

      do {
        const response = await emailApi.listByAssociation(
          associatedType,
          associatedUuid,
          cursor
        );

        runInAction(() => {
          response.items.forEach((email) => {
            this.emails.set(email.key, email);
            allEmailKeys.push(email.key);
          });
        });

        cursor = response.cursor || undefined;
      } while (cursor);

      runInAction(() => {
        this.emailsByAssociation.set(associationKey, allEmailKeys);
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch emails';
      });
    }
  }

  /**
   * Send an email
   */
  async sendEmail(params: SendEmailParams): Promise<Email | undefined> {
    runInAction(() => {
      this.sending = true;
      this.error = null;
    });

    try {
      const email = await emailApi.send(params);

      runInAction(() => {
        this.emails.set(email.key, email);

        // Update association index
        const associationKey = `${params.associated_type}:${params.associated_uuid}`;
        const existing = this.emailsByAssociation.get(associationKey) || [];
        this.emailsByAssociation.set(associationKey, [email.key, ...existing]);

        this.sending = false;
      });

      return email;
    } catch (err) {
      runInAction(() => {
        this.sending = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to send email';
      });
      return undefined;
    }
  }

  /**
   * Resend an email
   */
  async resendEmail(emailUuid: string): Promise<Email | undefined> {
    runInAction(() => {
      this.sending = true;
      this.error = null;
    });

    try {
      const email = await emailApi.resend(emailUuid);

      runInAction(() => {
        this.emails.set(email.key, email);
        this.sending = false;
      });

      return email;
    } catch (err) {
      runInAction(() => {
        this.sending = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to resend email';
      });
      return undefined;
    }
  }

  /**
   * Fetch email delivery history
   */
  async fetchHistory(emailUuid: string): Promise<void> {
    runInAction(() => {
      this.historyLoading = true;
    });

    try {
      let cursor: string | undefined;
      const allHistory: EmailHistory[] = [];

      do {
        const response = await emailApi.getHistory(emailUuid, cursor);
        allHistory.push(...response.items);
        cursor = response.cursor || undefined;
      } while (cursor);

      runInAction(() => {
        this.history.set(emailUuid, allHistory);
        this.historyLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.historyLoading = false;
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch email history';
      });
    }
  }

  /**
   * Receive realtime update for an email
   */
  receiveEmailUpdate(email: Email): void {
    runInAction(() => {
      this.emails.set(email.key, email);
    });
  }
}

export const emailStore = new EmailStore();
